import { createContext, useContext, useState, useEffect } from 'react';

/**
 * GridContext - Manages grid and snap-to-grid settings
 *
 * Features:
 * - Grid visibility toggle
 * - Snap-to-grid toggle
 * - Configurable grid size (10, 25, 50, 100 cm)
 * - Grid color and opacity settings
 * - localStorage persistence
 */

const GridContext = createContext(null);

// Default grid settings
const DEFAULT_GRID_SETTINGS = {
  gridEnabled: true,
  snapEnabled: true,
  gridSize: 50,
  gridColor: '#E5E7EB', // Gray-200 from design specs
  gridOpacity: 1.0,
};

// localStorage key
const STORAGE_KEY = 'garten-planer-grid-settings';

export const GridProvider = ({ children }) => {
  const [gridSettings, setGridSettings] = useState(() => {
    // Load from localStorage on initialization
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle new settings added in updates
        return { ...DEFAULT_GRID_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load grid settings from localStorage:', error);
    }
    return DEFAULT_GRID_SETTINGS;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(gridSettings));
    } catch (error) {
      console.error('Failed to save grid settings to localStorage:', error);
    }
  }, [gridSettings]);

  // Toggle grid visibility
  const toggleGrid = () => {
    setGridSettings((prev) => ({
      ...prev,
      gridEnabled: !prev.gridEnabled,
    }));
  };

  // Toggle snap-to-grid
  const toggleSnap = () => {
    setGridSettings((prev) => ({
      ...prev,
      snapEnabled: !prev.snapEnabled,
    }));
  };

  // Set grid size
  const setGridSize = (size) => {
    // Validate size is one of the allowed values
    const validSizes = [10, 25, 50, 100];
    if (!validSizes.includes(size)) {
      console.warn(`Invalid grid size: ${size}. Must be one of: ${validSizes.join(', ')}`);
      return;
    }
    setGridSettings((prev) => ({
      ...prev,
      gridSize: size,
    }));
  };

  // Set grid color
  const setGridColor = (color) => {
    setGridSettings((prev) => ({
      ...prev,
      gridColor: color,
    }));
  };

  // Set grid opacity
  const setGridOpacity = (opacity) => {
    // Validate opacity is between 0 and 1
    const validOpacity = Math.max(0, Math.min(1, opacity));
    setGridSettings((prev) => ({
      ...prev,
      gridOpacity: validOpacity,
    }));
  };

  // Reset to defaults
  const resetGridSettings = () => {
    setGridSettings(DEFAULT_GRID_SETTINGS);
  };

  const value = {
    // Current settings
    ...gridSettings,

    // Actions
    toggleGrid,
    toggleSnap,
    setGridSize,
    setGridColor,
    setGridOpacity,
    resetGridSettings,
  };

  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
};

/**
 * useGrid - Hook to access grid context
 *
 * Usage:
 * const { gridEnabled, snapEnabled, gridSize, toggleGrid, setGridSize } = useGrid();
 */
export const useGrid = () => {
  const context = useContext(GridContext);
  if (!context) {
    throw new Error('useGrid must be used within a GridProvider');
  }
  return context;
};
